import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HomepageEditor = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/homepage`);
      setContent(response.data.content);
    } catch (error) {
      toast.error('Errore nel caricamento dei contenuti');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/homepage`,
        content,
        { headers: getAuthHeader() }
      );
      toast.success('Contenuti salvati con successo!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const updateStat = (index, field, value) => {
    const newStats = [...content.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setContent({ ...content, stats: newStats });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...content.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    setContent({ ...content, benefits: newBenefits });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle>Sezione Hero</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Testo Badge
            </label>
            <Input
              value={content.heroBadgeText}
              onChange={(e) => updateField('heroBadgeText', e.target.value)}
              placeholder="Eleganza e Calore per la Tua Casa"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Titolo Principale
            </label>
            <Input
              value={content.heroTitle}
              onChange={(e) => updateField('heroTitle', e.target.value)}
              placeholder="Biocamini di Lusso"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sottotitolo
            </label>
            <Input
              value={content.heroSubtitle}
              onChange={(e) => updateField('heroSubtitle', e.target.value)}
              placeholder="per Ogni Ambiente"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Descrizione
            </label>
            <Textarea
              value={content.heroDescription}
              onChange={(e) => updateField('heroDescription', e.target.value)}
              rows={3}
              placeholder="Scopri la collezione DUGEZ..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Testo Bottone 1
              </label>
              <Input
                value={content.heroButton1Text}
                onChange={(e) => updateField('heroButton1Text', e.target.value)}
                placeholder="Esplora il Catalogo"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Testo Bottone 2
              </label>
              <Input
                value={content.heroButton2Text}
                onChange={(e) => updateField('heroButton2Text', e.target.value)}
                placeholder="Richiedi Consulenza"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Section */}
      <Card>
        <CardHeader>
          <CardTitle>Statistiche</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {content.stats.map((stat, index) => (
            <div key={index} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Valore {index + 1}
                </label>
                <Input
                  value={stat.value}
                  onChange={(e) => updateStat(index, 'value', e.target.value)}
                  placeholder="500+"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Etichetta {index + 1}
                </label>
                <Input
                  value={stat.label}
                  onChange={(e) => updateStat(index, 'label', e.target.value)}
                  placeholder="Clienti Soddisfatti"
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Benefits Section */}
      <Card>
        <CardHeader>
          <CardTitle>Sezione Vantaggi</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Titolo Sezione
            </label>
            <Input
              value={content.benefitsTitle}
              onChange={(e) => updateField('benefitsTitle', e.target.value)}
              placeholder="Perché Scegliere DUGEZ"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sottotitolo Sezione
            </label>
            <Input
              value={content.benefitsSubtitle}
              onChange={(e) => updateField('benefitsSubtitle', e.target.value)}
              placeholder="I biocamini che combinano..."
            />
          </div>
          
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-semibold">Vantaggi (4 cards)</h4>
            {content.benefits.map((benefit, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="font-medium text-gray-700">Vantaggio {index + 1}</div>
                <div className="grid gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Icona (Flame, Home, Leaf, Sparkles)
                    </label>
                    <Input
                      value={benefit.icon}
                      onChange={(e) => updateBenefit(index, 'icon', e.target.value)}
                      placeholder="Flame"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Titolo
                    </label>
                    <Input
                      value={benefit.title}
                      onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                      placeholder="Fiamma Vera"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Descrizione
                    </label>
                    <Textarea
                      value={benefit.description}
                      onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                      rows={2}
                      placeholder="Goditi il calore..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Products Section */}
      <Card>
        <CardHeader>
          <CardTitle>Sezione Prodotti</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Titolo
            </label>
            <Input
              value={content.productsTitle}
              onChange={(e) => updateField('productsTitle', e.target.value)}
              placeholder="I Nostri Biocamini"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sottotitolo
            </label>
            <Input
              value={content.productsSubtitle}
              onChange={(e) => updateField('productsSubtitle', e.target.value)}
              placeholder="Design italiano..."
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Etichetta Categorie
            </label>
            <Input
              value={content.productsCategoryLabel}
              onChange={(e) => updateField('productsCategoryLabel', e.target.value)}
              placeholder="Esplora per Categoria"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Bottone "Tutti"
              </label>
              <Input
                value={content.productsAllButtonText}
                onChange={(e) => updateField('productsAllButtonText', e.target.value)}
                placeholder="Tutti i Prodotti"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Testo Link Prodotto
              </label>
              <Input
                value={content.productsDiscoverMoreText}
                onChange={(e) => updateField('productsDiscoverMoreText', e.target.value)}
                placeholder="Scopri di Più"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Benefits Extra Section */}
      <Card>
        <CardHeader>
          <CardTitle>Vantaggi Extra</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Titolo Sezione Extra
            </label>
            <Input
              value={content.benefitsExtraTitle}
              onChange={(e) => updateField('benefitsExtraTitle', e.target.value)}
              placeholder="Il Lusso Accessibile..."
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              URL Immagine
            </label>
            <Input
              value={content.benefitsImageUrl}
              onChange={(e) => updateField('benefitsImageUrl', e.target.value)}
              placeholder="https://images.unsplash.com/..."
            />
            {content.benefitsImageUrl && (
              <img
                src={content.benefitsImageUrl}
                alt="Preview"
                className="mt-2 w-full max-w-md h-48 object-cover rounded-lg"
              />
            )}
          </div>
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Punti Vantaggi (4 punti)
            </label>
            {content.benefitsExtraPoints && content.benefitsExtraPoints.map((point, index) => (
              <Textarea
                key={index}
                value={point}
                onChange={(e) => {
                  const newPoints = [...content.benefitsExtraPoints];
                  newPoints[index] = e.target.value;
                  updateField('benefitsExtraPoints', newPoints);
                }}
                rows={2}
                placeholder={`Punto ${index + 1}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Contact Section Texts */}
      <Card>
        <CardHeader>
          <CardTitle>Testi Sezione Contatti</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Titolo
            </label>
            <Input
              value={content.contactTitle}
              onChange={(e) => updateField('contactTitle', e.target.value)}
              placeholder="Contattaci"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sottotitolo
            </label>
            <Textarea
              value={content.contactSubtitle}
              onChange={(e) => updateField('contactSubtitle', e.target.value)}
              rows={2}
              placeholder="Il nostro team è pronto..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Etichetta Email
              </label>
              <Input
                value={content.contactEmailLabel}
                onChange={(e) => updateField('contactEmailLabel', e.target.value)}
                placeholder="Email"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Testo "oppure"
              </label>
              <Input
                value={content.contactOrText}
                onChange={(e) => updateField('contactOrText', e.target.value)}
                placeholder="oppure"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Etichetta WhatsApp
            </label>
            <Input
              value={content.contactWhatsappLabel}
              onChange={(e) => updateField('contactWhatsappLabel', e.target.value)}
              placeholder="WhatsApp"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Testo Bottone WhatsApp
            </label>
            <Input
              value={content.contactWhatsappButtonText}
              onChange={(e) => updateField('contactWhatsappButtonText', e.target.value)}
              placeholder="Chatta con noi su WhatsApp"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sottotesto WhatsApp
            </label>
            <Input
              value={content.contactWhatsappSubtext}
              onChange={(e) => updateField('contactWhatsappSubtext', e.target.value)}
              placeholder="Oppure scrivici direttamente a:"
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Section */}
      <Card>
        <CardHeader>
          <CardTitle>Contatti</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Contatto
            </label>
            <Input
              type="email"
              value={content.contactEmail}
              onChange={(e) => updateField('contactEmail', e.target.value)}
              placeholder="info@dugez.com"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Numero WhatsApp
            </label>
            <Input
              value={content.whatsappNumber}
              onChange={(e) => updateField('whatsappNumber', e.target.value)}
              placeholder="+39 123 456 7890"
            />
            <p className="text-xs text-gray-500 mt-1">
              Formato: +39 seguito dal numero (es: +393123456789)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* SEO Section */}
      <Card>
        <CardHeader>
          <CardTitle>SEO - Ottimizzazione Google</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              SEO Titolo Homepage (60-70 caratteri)
            </label>
            <Input
              value={content.seoTitle}
              onChange={(e) => updateField('seoTitle', e.target.value)}
              placeholder="DUGEZ - Biocamini di Lusso | Design Italiano"
              maxLength={70}
            />
            <p className="text-xs text-gray-500 mt-1">{content.seoTitle?.length || 0}/70 caratteri</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              SEO Descrizione (150-160 caratteri)
            </label>
            <Textarea
              value={content.seoDescription}
              onChange={(e) => updateField('seoDescription', e.target.value)}
              rows={3}
              placeholder="Scopri i biocamini DUGEZ: design italiano, fiamma vera..."
              maxLength={160}
            />
            <p className="text-xs text-gray-500 mt-1">{content.seoDescription?.length || 0}/160 caratteri</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              SEO Keywords (separate da virgola)
            </label>
            <Textarea
              value={content.seoKeywords}
              onChange={(e) => updateField('seoKeywords', e.target.value)}
              rows={2}
              placeholder="biocamini, biocamino, camino bioetanolo, camino senza canna fumaria..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Salvataggio...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Salva Modifiche
            </>
          )}
        </Button>
      </div>
    </div>
  );
};